import { useState, useMemo, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { Calendar, ChevronDown, Eye, EyeOff, Package, TrendingDown, TrendingUp } from 'lucide-react';
import { CountrySelect } from '../../components/Flag';
import { MONTH_NAMES } from '../../config/months';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export const SellOutModule = ({ sellOutData }) => {
  const countries = useMemo(() => sellOutData && typeof sellOutData === 'object' ? Object.keys(sellOutData) : [], [sellOutData]);
  const [selectedCountry, setSelectedCountryRaw] = useState(() => localStorage.getItem('luye_sellout_country') || '');
  const setSelectedCountry = (v) => { localStorage.setItem('luye_sellout_country', v); setSelectedCountryRaw(v); setSelectedSkus([]); };
  const [viewMode, setViewMode] = useState('values');
  const [tableYear, setTableYear] = useState('');
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [showSkuLabels, setShowSkuLabels] = useState(false);
  const [showSkuDropdown, setShowSkuDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!showSkuDropdown) return;
    const handleClickOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowSkuDropdown(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSkuDropdown]);

  // Auto-select first country
  const activeCountry = countries.includes(selectedCountry) ? selectedCountry : countries[0] || '';
  const countryData = sellOutData?.[activeCountry];
  const months = countryData?.months || [];
  const skus = countryData?.skus || [];
  const individualSkus = useMemo(() => skus.filter(s => !s.isGroup), [skus]);
  const tableYears = useMemo(() => [...new Set(months.map(m => (m || '').split('-')[0]).filter(Boolean))].sort(), [months]);
  const effYear = tableYears.includes(tableYear) ? tableYear : (tableYears[tableYears.length - 1] || '');
  // Always render all 12 calendar slots (Jan–Dec) for the selected year, so months
  // land in their real position; idx is the data index in `months` or -1 if missing.
  const monthIdxLookup = useMemo(() => {
    const map = {};
    months.forEach((m, i) => { const [y, mo] = (m || '').split('-'); if (y) map[`${y}-${parseInt(mo, 10)}`] = i; });
    return map;
  }, [months]);
  const monthCols = MONTH_NAMES.map((name, mi) => ({ label: `${name} ${(effYear || '').slice(2)}`, idx: monthIdxLookup[`${effYear}-${mi + 1}`] ?? -1 }));
  const currency = countryData?.currency || 'LC';
  const isValues = viewMode === 'values';
  const activeSkus = selectedSkus.length > 0 ? selectedSkus : skus.map(s => s.name);
  const filteredSkus = skus.filter(s => activeSkus.includes(s.name));

  const formatMonth = (m) => {
    const [y, mo] = (m || '').split('-');
    return (MONTH_NAMES[parseInt(mo) - 1] || '') + ' ' + (y || '').slice(2);
  };

  const totalChartData = useMemo(() => {
    return months.map((m, i) => {
      let total = 0;
      individualSkus.forEach(sku => { total += (isValues ? sku.values[i] : sku.units[i]) || 0; });
      return { month: formatMonth(m), value: total };
    });
  }, [months, individualSkus, isValues]);

  const skuChartData = useMemo(() => {
    return months.map((m, i) => {
      const point = { month: formatMonth(m) };
      skus.forEach(sku => { point[sku.name] = (isValues ? sku.values[i] : sku.units[i]) || 0; });
      return point;
    });
  }, [months, skus, isValues]);

  if (!countryData || skus.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-lg font-semibold text-slate-700">No Sell Out data available</p>
          <p className="text-sm text-slate-500 mt-1">Upload a Sell Out Excel file from the Admin panel to see data here.</p>
        </div>
      </div>
    );
  }

  const toggleSku = (name) => {
    if (selectedSkus.length === 0) {
      setSelectedSkus(skus.filter(s => s.name !== name).map(s => s.name));
    } else if (selectedSkus.includes(name)) {
      setSelectedSkus(selectedSkus.filter(n => n !== name));
    } else {
      setSelectedSkus([...selectedSkus, name]);
    }
  };
  const selectAllSkus = () => setSelectedSkus([]);

  const formatYAxis = (v) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${Math.round(v / 1000)}K`;
    return v.toLocaleString();
  };

  const formatTooltip = (v) => isValues ? `${currency} ${v?.toLocaleString() || 0}` : `${v?.toLocaleString() || 0} units`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Market performance</p><h2 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 tracking-tight">Sell Out</h2><p className="text-sm text-slate-500 mt-1">Monthly sell out by country and SKU — IQVIA data provided by our partner</p></div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Country:</span>
          <CountrySelect
            value={activeCountry}
            onChange={setSelectedCountry}
            options={countries.map(c => ({ value: c, label: `${sellOutData[c]?.name || c} (${sellOutData[c]?.currency})`, country: c }))}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">View:</span>
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button onClick={() => setViewMode('values')} className={`px-3 py-1 rounded-md text-sm font-medium ${viewMode === 'values' ? 'bg-white shadow text-blue-600' : 'text-slate-600'}`}>💵 Values ({currency})</button>
            <button onClick={() => setViewMode('units')} className={`px-3 py-1 rounded-md text-sm font-medium ${viewMode === 'units' ? 'bg-white shadow text-blue-600' : 'text-slate-600'}`}>📦 Units</button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {(() => {
        const getData = (sku) => isValues ? sku.values : sku.units;
        const monthlyTotals = months.map((_, i) => individualSkus.reduce((s, sku) => s + (getData(sku)[i] || 0), 0));
        const n = monthlyTotals.length;

        // Q vs Q-1 (last 3 months vs 3 before that)
        const qCurrent = monthlyTotals.slice(-3).reduce((s, v) => s + v, 0);
        const qPrev = monthlyTotals.slice(-6, -3).reduce((s, v) => s + v, 0);
        const qChange = qPrev > 0 ? ((qCurrent - qPrev) / qPrev * 100).toFixed(1) : 0;

        // MAT vs MAT-1 (last 12 months vs 12 before that)
        const matCurrent = monthlyTotals.slice(-12).reduce((s, v) => s + v, 0);
        const matPrev = monthlyTotals.slice(-24, -12).reduce((s, v) => s + v, 0);
        const matChange = matPrev > 0 ? ((matCurrent - matPrev) / matPrev * 100).toFixed(1) : 0;

        // Latest month vs same month last year
        const latestVal = monthlyTotals[n - 1] || 0;
        const lyVal = n >= 13 ? (monthlyTotals[n - 13] || 0) : 0;
        const yoyChange = lyVal > 0 ? ((latestVal - lyVal) / lyVal * 100).toFixed(1) : 0;

        // Q vs same Q last year (last 3 months vs same 3 months one year ago)
        const qCurrentLY = n >= 15 ? monthlyTotals.slice(-15, -12).reduce((s, v) => s + v, 0) : 0;
        const qYoyChange = qCurrentLY > 0 ? ((qCurrent - qCurrentLY) / qCurrentLY * 100).toFixed(1) : 0;

        const KpiCard = ({ title, change, currentVal, prevVal, icon: Icon, iconColor, subtitle }) => (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">{title}</span>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className={`text-2xl font-bold flex items-center gap-1 ${parseFloat(change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {parseFloat(change) >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              {change > 0 ? '+' : ''}{change}%
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {formatYAxis(currentVal)} <span className="text-slate-400">vs</span> {formatYAxis(prevVal)}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>
          </div>
        );

        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard title="Q vs Q-1" change={qChange} currentVal={qCurrent} prevVal={qPrev} icon={TrendingUp} iconColor="text-blue-500" subtitle="last 3 months vs previous 3" />
            <KpiCard title="MAT vs MAT-1" change={matChange} currentVal={matCurrent} prevVal={matPrev} icon={Calendar} iconColor="text-purple-500" subtitle="moving annual total" />
            <KpiCard title="Latest Month vs LY" change={yoyChange} currentVal={latestVal} prevVal={lyVal} icon={TrendingUp} iconColor="text-green-500" subtitle={`${months[n-1] ? formatMonth(months[n-1]) : ''} vs ${months[n-13] ? formatMonth(months[n-13]) : 'N/A'}`} />
            <KpiCard title="Q vs Q LY" change={qYoyChange} currentVal={qCurrent} prevVal={qCurrentLY} icon={Calendar} iconColor="text-orange-500" subtitle="current quarter vs same quarter last year" />
          </div>
        );
      })()}

      {/* Total Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">
          <TrendingUp className="w-4 h-4 inline mr-2 text-green-500" />
          Total Sell Out — {countryData.name} ({isValues ? currency : 'Units'})
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={totalChartData} margin={{ top: 25, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatYAxis} />
            <Tooltip formatter={(v) => [formatTooltip(v), 'Total']} />
            <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} name="Total">
              <LabelList dataKey="value" position="top" fontSize={10} fontWeight={600} fill="#2563eb" offset={10} formatter={(v) => v > 0 ? formatYAxis(v) : ''} />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Per-SKU Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">
            <Package className="w-4 h-4 inline mr-2 text-purple-500" />
            Sell Out by SKU — {countryData.name} ({isValues ? currency : 'Units'})
          </h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowSkuLabels(!showSkuLabels)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${showSkuLabels ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
              {showSkuLabels ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Labels
            </button>
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setShowSkuDropdown(!showSkuDropdown)} className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-medium bg-white hover:bg-slate-50">
                SKUs ({activeSkus.length}/{skus.length}) <ChevronDown className="w-3 h-3" />
              </button>
              {showSkuDropdown && (
                <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSkuDropdown(false)}></div>
                <div className="absolute right-0 top-9 bg-white border border-slate-200 rounded-lg shadow-xl z-50 min-w-56 py-1">
                  <div className="flex items-center justify-between px-3 py-1.5">
                    <button onClick={selectAllSkus} className="text-xs font-medium text-blue-600 hover:underline">Select All</button>
                    <button onClick={() => setSelectedSkus(['__none__'])} className="text-xs font-medium text-slate-400 hover:underline">Deselect All</button>
                  </div>
                  <div className="border-t border-slate-100 my-1"></div>
                  {skus.map((sku, idx) => (
                    <label key={sku.name} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={activeSkus.includes(sku.name)} onChange={() => toggleSku(sku.name)} className="w-3.5 h-3.5 rounded" />
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                      <span className="text-xs text-slate-700">{sku.name.replace('SEROQUEL ', '').replace('TABL ', '').replace('L.P. ', '')}</span>
                    </label>
                  ))}
                </div>
                </>
              )}
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={skuChartData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatYAxis} />
            <Tooltip formatter={(v, name) => [formatTooltip(v), name]} />
            {filteredSkus.map((sku, idx) => {
              const colorIdx = skus.findIndex(s => s.name === sku.name);
              return (
                <Line key={sku.name} type="monotone" dataKey={sku.name} stroke={COLORS[colorIdx % COLORS.length]} strokeWidth={2} dot={{ r: 1.5 }}>
                  {showSkuLabels && <LabelList dataKey={sku.name} position="top" fontSize={10} fontWeight={600} fill={COLORS[colorIdx % COLORS.length]} offset={10} formatter={(v) => v > 0 ? formatYAxis(v) : ''} />}
                </Line>
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-slate-700">Monthly Detail — {countryData.name} ({isValues ? currency : 'Units'})</h3>
          {tableYears.length > 1 && (
            <div className="flex bg-slate-100 rounded-lg p-1">
              {tableYears.map(y => (
                <button key={y} onClick={() => setTableYear(y)} className={`px-3 py-1 rounded-md text-xs font-medium ${effYear === y ? 'bg-white shadow text-blue-600' : 'text-slate-600'}`}>{y}</button>
              ))}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600 sticky left-0 bg-slate-50 min-w-48">SKU</th>
                {monthCols.map(({ label }) => <th key={label} className="text-right p-2 font-medium text-slate-600 whitespace-nowrap">{label}</th>)}
                <th className="text-right p-2 font-bold text-slate-700 bg-blue-50 whitespace-nowrap">Total</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((sku, idx) => {
                const data = isValues ? sku.values : sku.units;
                return (
                  <tr key={sku.name} className="border-t hover:bg-slate-50" style={{ borderLeft: `3px solid ${COLORS[idx % COLORS.length]}` }}>
                    <td className="p-3 font-medium text-slate-800 sticky left-0 bg-white text-xs">{sku.name}</td>
                    {monthCols.map(({ idx }, ci) => {
                      const v = idx >= 0 ? (data[idx] || 0) : null;
                      return <td key={ci} className={`text-right p-2 text-xs ${!v ? 'text-slate-300' : 'text-slate-700'}`}>{!v ? '—' : isValues ? Math.round(v).toLocaleString() : v.toLocaleString()}</td>;
                    })}
                    <td className="text-right p-2 text-xs font-bold bg-blue-50 text-slate-800">{(() => { const rt = monthCols.reduce((s, c) => s + (c.idx >= 0 ? (data[c.idx] || 0) : 0), 0); return rt ? Math.round(rt).toLocaleString() : '—'; })()}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 bg-slate-100 font-bold">
                <td className="p-3 text-slate-800 sticky left-0 bg-slate-100">TOTAL</td>
                {monthCols.map(({ idx }, ci) => {
                  const total = idx >= 0 ? individualSkus.reduce((s, sku) => s + ((isValues ? sku.values[idx] : sku.units[idx]) || 0), 0) : null;
                  return <td key={ci} className="text-right p-2 text-xs text-slate-800">{total ? Math.round(total).toLocaleString() : '—'}</td>;
                })}
                <td className="text-right p-2 text-xs text-slate-900 bg-blue-100">{(() => { const grand = monthCols.reduce((s, c) => s + (c.idx >= 0 ? individualSkus.reduce((ss, sku) => ss + ((isValues ? sku.values[c.idx] : sku.units[c.idx]) || 0), 0) : 0), 0); return grand ? Math.round(grand).toLocaleString() : '—'; })()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
